import { Button, ButtonType } from "components/Button/Button";
import MediaType from "components/MediaType/MediaType";
import { useModal } from "components/Modal/context/ModalContext";
import { GetLockedContentModalData, GetLockedLearningJourneyModalData } from "components/Modal/utils/lockedContentModalData";
import useBranding from "hooks/useBranding";
import useUserData from "hooks/useUserData";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ModuleCustomType, ModuleStateType } from "utils/constants";
import "./ModuleItem.scss";
import ProgressIcon from "./ProgressIcon";
import SubModuleAccordion from "./SubModuleAccordion";
import { UserRoles } from "utils/userRoles";
import { faShareNodes } from "@fortawesome/free-solid-svg-icons";
import ShareModal from "components/ShareModal/ShareModal";
import { ModalButtons } from "components/Modal/ModalButtons";

const ModuleItem = ({
    course,
    module,
    expandedModuleId,
    onExpand,
    supressExpand,
    previewMode = false, 
    previewLockedLearningJourneys = false
}) => {
    const { contactDetails } = useBranding();
    const { userData } = useUserData();
    const { openModal } = useModal();
    const navigate = useNavigate();
    const headerRef = useRef(null);

    const handleOpenLockedByUpsellModal = () => {
        if (previewMode) {
            return;
        }

        const data = GetLockedContentModalData(
            userData.UserLevel === UserRoles.Learner,
            contactDetails.Number,
            contactDetails.Email
        );

        openModal(data.title, data.content);
    };

    const handleOpenLockedLearningJourneyModal = (ruleType) => {
        if (previewMode) {
            return;
        }

        const data = GetLockedLearningJourneyModalData(ruleType);
        openModal(data.title, data.content);
    };

    const handleClick = () => {
        if (previewMode) {
            return;
        }

        if (!module?.LastViewed?.Id) {
            return;
        }

        navigate(`/player#courseId-${course.Id}-moduleId-${module.Id}-submoduleId-${module.LastViewed.Id}`);
    };

    const showAccordion = () => {
        return module.SubSubContentCount > 1 || module.CustomTypeId === ModuleCustomType.DeviceTraining;
    };

    const getButtonText = (progress) => {
        switch (progress) {
            case 0:
                return "Start";
            case 100:
                return "Restart";
            default:
                return "Continue";
        }
    };

    const renderButton = () => {
        if (previewMode) {
            return module.ContentType.length
                ? <Button type={ButtonType.Primary} text="Start" />
                : null;
        }

        if (module.State === ModuleStateType.Enabled) {
            return module.ContentType.length
                ? <Button type={ButtonType.Primary} text={getButtonText(module.Progress)} onClick={handleClick} />
                : null;
        }

        if (module.State === ModuleStateType.DisabledUpsell) {
            return <Button type={ButtonType.Secondary} text="Unlock" onClick={handleOpenLockedByUpsellModal} />;
        }

        return null;
    };

    const buildModuleShareLink = () => {
        return `/course-view#courseId-${course.Id}-moduleId-${module.Id}`;
    };

    const handleOpenShareModal = ({ shareLink, contentName }) => {
        if (previewMode) {
            return;
        }

        openModal(
            "Share",
            <ShareModal shareLink={shareLink} contentName={contentName} />,
            ModalButtons.None
        );
    };

    return (
        <div className="list-view-item-item" id={`courseId-${course.Id}-moduleId-${module.Id}`}>
            <div className="list-view-item-item-progress">
                <ProgressIcon
                    state={module.State}
                    hasContent={module.ContentType.length}
                    className="list-view-item-item-progress-container"
                    progressData={{
                        hasProgress: module.HasProgress,
                        value: module.Progress.Value,
                        asPercentage: true,
                        success: module.Progress.Success
                    }}
                    handleClick={handleClick}
                    handleOpenLockModal={handleOpenLockedByUpsellModal}
                    handleOpenCourseTraversalModal={handleOpenLockedLearningJourneyModal}
                    previewMode={previewMode}
                />
            </div>

            <div className={`list-view-item-item-details ${showAccordion() ? "" : "extra-padding"}`}>
                <div className="list-view-item-item-details-topline">
                    <div className="list-view-item-item-details-topline-title">
                        <h3 ref={headerRef}>{module.Name}</h3>
                        {module.Duration && <p className="small-text">({module.Duration})</p>}
                        <MediaType type={module.ContentType} />
                    </div>
                    <div className="list-view-item-item-details-topline-share">
                        <Button
                            onClick={() =>
                                handleOpenShareModal({
                                    shareLink: buildModuleShareLink(),
                                    contentName: `${course.Name} > ${module.Name}`
                                })
                            }
                            type={ButtonType.Tertiary}
                            text="Share"
                            icon={faShareNodes}
                        />
                    </div>
                </div>

                <p className="list-view-item-item-details-description">{module.Description}</p>

                <div className="action-button">
                    {renderButton()}
                </div>

                {showAccordion() && (
                    <SubModuleAccordion
                        expanded={module.Id === expandedModuleId}
                        courseId={course.Id}
                        moduleId={module.Id}
                        submoduleCount={module.SubSubContentCount}
                        guruUrl={module.GuruUrl}
                        webcast={module.HasWebcast}
                        contactNumber={contactDetails.Number}
                        state={module.State}
                        handleOpenLockModal={handleOpenLockedByUpsellModal}
                        handleOpenCourseTraversalModal={handleOpenLockedLearningJourneyModal}
                        onExpand={() => onExpand(module.Id)}
                        headerRef={headerRef}
                        courseName={course.Name}
                        supressExpand={supressExpand}
                        previewMode={previewMode}
                        previewSubmodules={module.SubContent ?? []}
                        previewLockedLearningJourneys={previewLockedLearningJourneys}
                    />
                )}
            </div>
        </div>
    );
};

export default ModuleItem;